import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Project } from './types'
import { Progress } from '@aws-sdk/lib-storage'
import FileUploader from 'app/services/FileUploader'
import { isMpoProjectInProgress } from 'app/helpers'

export class MpoProjectsStore {
  loadingList: boolean = false
  deleting: boolean = false
  list: Project[] = []
  entries: {
    [key: string]: Project
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      deleting: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      getData: action,
      delete: action,
      runProject: action,
      setProject: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
      inProgressProjects: computed,
    })
  }

  get inProgressProjects() {
    return this.list.filter(isMpoProjectInProgress)
  }

  reset = () => {
    this.clearList()
    this.deleting = false
    this.entries = {}
    this.cancelTokenSource = () => {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  getUploadUrl: (type: string) => Promise<string> = async (filename) => {
    return await axios(
      '/api/v1/mpo-project/pdb/file/upload/url?fileName=' + filename
    ).then(({ data }) => data)
  }

  uploadFile = async (fileRef: any) => {
    const onProgress = (event: Progress) => {
      // @ts-ignore
      fileRef.progress = Math.round((100 * event.loaded) / event.total)
      // Re-render the file list to reflect the new progress
      fileRef.updateView()
    }

    try {
      fileRef.url = await FileUploader.uploadFile(
        fileRef.file,
        this.getUploadUrl,
        onProgress
      )
      fileRef.uploaded = true
      fileRef.uploading = false
      // Re-render the file list to reflect the new changes (uploaded = true)
      fileRef.updateView()
      return fileRef.url
    } catch (error) {
      toast.error('Failed to upload file. Please try again.')
      fileRef.progress = 0
      fileRef.error = true
      throw error
    }
  }

  handlePdbFiles = async (files: any[]) => {
    const pdbFiles = []
    for (const file of files) {
      // @ts-ignore
      if (file?.id) {
        // @ts-ignore
        pdbFiles.push({ id: file.id, fileName: file.fileName })
      } else {
        // @ts-ignore
        let signedUrl = file.url
        if (!signedUrl) {
          signedUrl = await this.uploadFile(file)
        }
        const splitedUrl = signedUrl.split('?')[0].split('/')
        let fileName = splitedUrl[splitedUrl.length - 1]
        fileName = decodeURI(fileName)
        pdbFiles.push({ fileName })
      }
    }
    return pdbFiles
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: any) => {
    const pdbFiles = await this.handlePdbFiles(data.pdbFiles)

    return await axios
      .post('/api/v1/mpo-project', {
        ...data,
        pdbFiles: pdbFiles,
      })
      .then((response) => {
        toast.success('Created successfully')
        return response
      })
  }

  update = async (id: number, data: any) => {
    const pdbFiles = await this.handlePdbFiles(data.pdbFiles)
    return await axios
      .put('/api/v1/mpo-project/' + id, {
        ...data,
        pdbFiles: pdbFiles,
      })
      .then((response) => {
        toast.success('Updated successfully')
        return response
      })
  }

  delete = async (id: number) => {
    this.deleting = true
    await axios
      .delete('/api/v1/mpo-project/' + id)
      .then(({ data }) => {
        toast.success(
          'Delete is started. You will be notified when the process is completed'
        )
        this.setProject(data)
      })
      .finally(() => {
        this.deleting = false
      })
  }

  getById = async (id: number) => {
    return await axios(
      `/api/v1/mpo-project/${id}?withFeaturesLabels=true`
    ).then(({ data }) => {
      this.setProject(data)
      return data
    })
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }

    if (filters.withIdsFilter && !filters.ids) {
      this.clearList()
      return
    }

    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.searchValue
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/api/v1/mpo-project`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
            this.list.forEach((item) => (this.entries[`${item.id}`] = item))
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  searchByName = async (name: string) => {
    return await axios
      .get(`/api/v1/mpo-project`, {
        params: {
          name,
          size: 10,
        },
      })
      .then(({ data }) => data?.content)
  }

  isNameExists = async (name: string) => {
    return await axios(`/api/v1/mpo-project/name/exists?name=${name}`).then(
      ({ data }) => data
    )
  }

  getProjectFeatureStats = async (id: number, feature: string) => {
    return await axios(`/api/v1/mpo-project/${id}/${feature}/stats`).then(
      ({ data }) => data
    )
  }

  runProject = (id: number) => {
    return axios
      .put('/api/v1/mpo-project/' + id + '/execute/')
      .then(({ data }) => {
        toast.success('Running successfully')
        this.setProject(data)
      })
  }
  cancelProject = (id: number) => {
    return axios
      .put('/api/v1/mpo-project/' + id + '/cancel')
      .then(({ data }) => {
        toast.success('Started canceling')
        this.setProject(data)
      })
  }
  setProject = (project: Project) => {
    const index = this.list.findIndex((item: Project) => item.id === project.id)
    if (index !== -1) {
      this.list[index] = project
    }
    this.entries[`${project.id}`] = project
  }

  getLabels = async (id: number) => {
    return await axios(`/api/v1/mpo-project/${id}/labels`).then(
      ({ data }) => data.labels
    )
  }

  updateLabels = async (id: number, labels: object) => {
    return await axios
      .put(`/api/v1/mpo-project/${id}/labels`, { labels })
      .then((response) => {
        toast.success('Updated successfully')
        return response
      })
  }

  downloadProjectResult = async (id: number, sort?: object, filters?: any) => {
    const body = {
      sort,
      filters,
    }
    await axios
      .post('/api/v1/mpo-project/' + id + '/csv', body)
      .then(({ data, headers }) => {
        const blob = new Blob([data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        const disposition = headers['content-disposition']
        const matches = /"([^"]*)"/.exec(disposition)
        const filename = matches != null && matches[1] ? matches[1] : 'data.csv'

        link.download = filename
        link.click()
      })
  }

  exportResultAsDataset = async (id: number, name: string) => {
    return await axios
      .put(`/api/v1/mpo-project/${id}/export`, { datasetName: name })
      .then(({ data }) => data)
  }

  getEstimatedCost = async () => {
    return await axios(`/api/v1/mpo-project/cost`).then(({ data }) => data)
  }

  // Returns the cost of the completed tasks in the operation being executed on the object 
  getCostOfCompletedTasks = async (id: number) => {
    return await axios(`/api/v1/mpo-project/${id}/current-operation-cost`).then(
      ({ data }) => data
    )
  }

  getProjectsByIds = async (ids: number[]) => {
    return await axios.get('/api/v1/mpo-project', {
      params: {
        ids: ids.join(','),
        size: 1000,
      },
    }).then(
      ({ data }) => data.content as Project[]
    )
  }

  refetchListIds = async (ids: number[]) => {
    const exitsIds = ids.filter((id) => this.list.findIndex((item) => item.id === id) !== -1)
    const projects = await this.getProjectsByIds(exitsIds)
    projects.forEach((project) => {
      const index =  this.list.findIndex((item) => item.id === project.id)
      runInAction(() => {
        this.list[index] = project
      })
    });
  }

}

export const mpoProjectsStore = new MpoProjectsStore()
